import { Form, Formik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { useMemo } from "react";
import { PMDialog } from "../../../utils/PMDialog";
import { PMField } from "../../../utils/PMField";
import { sendDeviation } from "../../../../api/quality/deviation";

const buildRecipientOptions = (deviation) => {
  const customer = deviation?.customer;
  const project = deviation?.project;
  const contact = project?.contact || deviation?.contact;
  const workOrder = deviation?.work_order;
  const projectManager = project?.project_manager;
  const internalProjectLeader = workOrder?.responsible_participant?.user;
  const projectLeader =
    projectManager ||
    workOrder?.responsible_executor_participant?.user ||
    internalProjectLeader;

  const options = [
    {
      key: "project_leader",
      label: "Prosjektleder",
      name: projectLeader?.displayTitle,
      email: projectLeader?.email,
    },
    {
      key: "internal_project_leader",
      label: "Intern prosjektleder",
      name: internalProjectLeader?.displayTitle,
      email: internalProjectLeader?.email,
    },
    {
      key: "contact",
      label: "Kontakt hos kunde",
      name: contact?.displayTitle || contact?.name,
      email: contact?.email,
    },
    {
      key: "customer",
      label: "Kunde",
      name: customer?.displayTitle || customer?.name,
      email: customer?.email,
    },
  ].filter((option) => option.email);

  const seen = new Set();

  return options.filter((option) => {
    const email = option.email?.toLowerCase();
    if (!email || seen.has(email)) return false;
    seen.add(email);
    return true;
  });
};

const buildProjectContactOptions = (deviation, recipientOptions) => {
  const seenEmails = new Set(
    (recipientOptions || []).map((option) => option.email?.toLowerCase()).filter(Boolean)
  );

  return (deviation?.project?.project_contacts || [])
    .filter((projectContact) => projectContact?.id && projectContact?.email)
    .filter((projectContact) => {
      const email = projectContact.email.toLowerCase();
      if (seenEmails.has(email)) return false;
      seenEmails.add(email);
      return true;
    })
    .map((projectContact) => ({
      id: projectContact.id,
      label: projectContact.contact_type || "Prosjektkontakt",
      name:
        projectContact.displayTitle ||
        projectContact.user?.displayTitle ||
        projectContact.contact?.displayTitle ||
        projectContact.customer_contact?.displayTitle,
      email: projectContact.email,
      mobile: projectContact.mobile,
      sourceType: projectContact.source_type,
    }));
};

export const DeviationSendDialog = ({
  open,
  onClose,
  deviation,
  onSent,
}) => {
  const recipientOptions = useMemo(
    () => buildRecipientOptions(deviation),
    [deviation]
  );
  const projectContactOptions = useMemo(
    () => buildProjectContactOptions(deviation, recipientOptions),
    [deviation, recipientOptions]
  );
  const projectManagerOption = recipientOptions.find(
    (option) => option.key === "project_leader"
  );

  const toggleRecipient = (formik, recipientKey) => {
    const current = formik.values.recipient_types || [];
    const next = current.includes(recipientKey)
      ? current.filter((value) => value !== recipientKey)
      : [...current, recipientKey];

    formik.setFieldValue("recipient_types", next);
  };

  const toggleProjectContact = (formik, projectContactId) => {
    const current = formik.values.project_contact_ids || [];
    const next = current.includes(projectContactId)
      ? current.filter((value) => value !== projectContactId)
      : [...current, projectContactId];

    formik.setFieldValue("project_contact_ids", next);
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title="Send informasjon om avvik"
      size="3xl"
    >
      <Formik
        enableReinitialize
        initialValues={{
          id: deviation?.id,
          recipient_types: [],
          project_contact_ids: [],
          custom_name: "",
          custom_email: "",
          message: "",
        }}
        validationSchema={Yup.object({
          custom_email: Yup.string().email("Ugyldig e-post"),
        })}
        onSubmit={async (values, helpers) => {
          try {
            const response = await sendDeviation(values);

            if (response?.status !== 200) {
              throw new Error(
                response?.errors?.[0] || "Kunne ikke sende informasjon om avviket"
              );
            }

            toast.success("Avviket ble sendt");
            onSent?.(response?.deviation || deviation);
            helpers.setSubmitting(false);
            onClose?.();
          } catch (error) {
            console.error(error);
            toast.error(error?.message || "Kunne ikke sende");
            helpers.setSubmitting(false);
          }
        }}
      >
        {(formik) => (
          <Form className="space-y-6">
            <div className="space-y-4">
              <div className="flex flex-col">
                <span className="mb-2 text-sm text-gray-500">
                  Velg mottakere
                </span>
                <span className="isolate inline-flex flex-wrap rounded-md shadow-sm">
                  {recipientOptions.map((option, index) => {
                    const checked = formik.values.recipient_types.includes(
                      option.key
                    );

                    return (
                      <button
                        key={option.key}
                        type="button"
                        onClick={() => toggleRecipient(formik, option.key)}
                        className={`relative inline-flex items-center px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 focus:z-10 ${
                          index === 0 ? "rounded-l-md" : ""
                        } ${
                          index === recipientOptions.length - 1
                            ? "rounded-r-md"
                            : ""
                        } ${
                          checked
                            ? "z-10 bg-blue-50 text-blue-700"
                            : "bg-white text-gray-900 hover:bg-gray-50"
                        } ${
                          index > 0 ? "-ml-px" : ""
                        }`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </span>
              </div>

              {recipientOptions.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2">
                  {recipientOptions
                    .filter((option) =>
                      formik.values.recipient_types.includes(option.key)
                    )
                    .map((option) => (
                      <div
                        key={option.key}
                        className="rounded-xl border border-blue-100 bg-blue-50 p-4"
                      >
                        <p className="text-sm font-medium text-gray-900">
                          {option.label}
                        </p>
                        <p className="mt-1 text-sm text-gray-600">
                          {option.name || "Uten navn"}
                        </p>
                        <p className="text-sm text-gray-500">{option.email}</p>
                      </div>
                    ))}
                </div>
              )}

              {projectContactOptions.length > 0 && (
                <div className="space-y-3">
                  <div>
                    <h4 className="text-sm font-semibold text-gray-900">
                      Prosjektkontakter
                    </h4>
                    <p className="mt-1 text-sm text-gray-500">
                      Ekstra kontakter hentet fra prosjektets kontaktliste.
                    </p>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    {projectContactOptions.map((option) => {
                      const checked = formik.values.project_contact_ids.includes(
                        option.id
                      );

                      return (
                        <label
                          key={option.id}
                          className={`flex cursor-pointer items-start gap-3 rounded-xl border p-4 transition-colors ${
                            checked
                              ? "border-cyan-300 bg-cyan-50"
                              : "border-gray-200 bg-white hover:bg-gray-50"
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleProjectContact(formik, option.id)}
                            className="mt-1 h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                          />
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-medium text-gray-900">
                                {option.name || "Uten navn"}
                              </p>
                              <span className="inline-flex rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-600">
                                {option.label}
                              </span>
                            </div>
                            <p className="mt-1 text-sm text-gray-500">
                              {option.email}
                              {option.mobile ? ` / ${option.mobile}` : ""}
                            </p>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <div>
                <p className="text-sm text-gray-500">
                  Prosjektleder og kontakt hentes fra prosjektet dersom de er registrert der.
                </p>
                <p className="mt-2 text-sm text-gray-500">
                  Det sendes alltid kopi til deg som avsender
                  {projectManagerOption?.name
                    ? `, i tillegg til valgt mottaker. Prosjektleder på prosjektet er ${projectManagerOption.name}.`
                    : ", i tillegg til valgt mottaker."}
                </p>
              </div>
            </div>

            <div className="grid gap-4 rounded-xl border border-gray-200 bg-gray-50 p-4 sm:grid-cols-2">
              <div className="sm:col-span-2">
                <h4 className="text-sm font-semibold text-gray-900">
                  Valgfri mottaker
                </h4>
              </div>
              <PMField
                name="custom_name"
                label="Navn"
                type="text"
                {...formik}
              />
              <PMField
                name="custom_email"
                label="E-post"
                type="email"
                {...formik}
              />
            </div>

            <PMField
              name="message"
              label="Melding"
              multiline
              rows={6}
              placeholder="Skriv en kort melding til mottakeren"
              {...formik}
            />

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Avbryt
              </button>
              <button
                type="button"
                onClick={formik.submitForm}
                disabled={formik.isSubmitting}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Send
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </PMDialog>
  );
};
